import React, { lazy, Suspense, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Login/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import RequestsList from './pages/RequestsList/RequestsList';
import RequestDetail from './pages/RequestDetail/RequestDetail';
import CreateRequest from './pages/CreateRequest/CreateRequest';
import EditRequest from './pages/EditRequest/EditRequest';
import OfferCreate from './pages/OfferCreate/OfferCreate';
import { injectsApi, InjectScreenType } from './services/api';
import './App.css';

// Lazy load heavy components
const Users = lazy(() => import('./pages/Users/Users'));
const Assets = lazy(() => import('./pages/Assets/Assets'));
const StartPoints = lazy(() => import('./pages/StartPoints/StartPoints'));
const Hospital = lazy(() => import('./pages/Hospital/Hospital'));
const Schedules = lazy(() => import('./pages/Schedules/Schedules'));
const ScheduleDetail = lazy(() => import('./pages/ScheduleDetail/ScheduleDetail'));
const Documentation = lazy(() => import('./pages/Documentation/Documentation'));
const ChangePassword = lazy(() => import('./pages/ChangePassword/ChangePassword'));
const TransportPlanning = lazy(() => import('./pages/TransportPlanning/TransportPlanning'));
const TransportBooking = lazy(() => import('./pages/TransportBooking/TransportBooking'));
const CapacityPlanning = lazy(() => import('./pages/CapacityPlanning/CapacityPlanning'));
const NotificationSettings = lazy(() => import('./pages/NotificationSettings/NotificationSettings'));
const Settings = lazy(() => import('./pages/Settings/Settings'));
const Map = lazy(() => import('./pages/Map/Map'));
// Statistics page removed — redirects to exercise-analytics
const PatientImport = lazy(() => import('./pages/PatientImport/PatientImport'));
// Heavy components with plotly.js - lazy load to reduce initial bundle
const FlowDiagram = lazy(() => import('./pages/FlowDiagram/FlowDiagram'));
const Missions = lazy(() => import('./pages/Missions/Missions'));
// Exercise & Template system
const AssetTemplates = lazy(() => import('./pages/AssetTemplates/AssetTemplates'));
const DayTemplates = lazy(() => import('./pages/DayTemplates/DayTemplates'));
const ExerciseControl = lazy(() => import('./pages/ExerciseControl/ExerciseControl'));
const MissionDetail = lazy(() => import('./pages/MissionDetail/MissionDetail'));
const TransportExtension = lazy(() => import('./pages/TransportExtension/TransportExtension'));
const AuditLogs = lazy(() => import('./pages/AuditLogs/AuditLogs'));
const Notifications = lazy(() => import('./pages/Notifications/Notifications'));
const ManageOffers = lazy(() => import('./pages/ManageOffers/ManageOffers'));
const MyPatients = lazy(() => import('./pages/MyPatients/MyPatients'));
const MyTransportPatients = lazy(() => import('./pages/MyTransportPatients/MyTransportPatients'));
const DataSync = lazy(() => import('./pages/DataSync/DataSync'));
const HealthDashboard = lazy(() => import('./pages/HealthDashboard/HealthDashboard'));
const AnnouncementTicker = lazy(() => import('./pages/AnnouncementTicker/AnnouncementTicker'));
// ExerciseStats page removed — redirects to exercise-analytics
const ExerciseAnalytics = lazy(() => import('./pages/ExerciseAnalytics/ExerciseAnalytics'));
const ExconStatistics = lazy(() => import('./pages/ExconStatistics/ExconStatistics'));
const AssetContracts = lazy(() => import('./pages/AssetContracts/AssetContracts'));
const NationAnalytics = lazy(() => import('./pages/NationAnalytics/NationAnalytics'));
const DataConsistency = lazy(() => import('./pages/DataConsistency/DataConsistency'));
const InjectControl = lazy(() => import('./pages/InjectControl/InjectControl'));

function MaintenanceScreen() {
  const { t } = useTranslation();
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      background: 'var(--bg-page)',
      textAlign: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'var(--bg-surface)',
        borderRadius: '12px',
        padding: '48px',
        maxWidth: '480px',
        boxShadow: 'var(--shadow-lg)',
        border: '2px solid var(--color-warning)'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#9888;</div>
        <h2 style={{ margin: '0 0 12px 0', color: 'var(--text-primary)' }}>{t('maintenance.title')}</h2>
        <p style={{ color: 'var(--text-secondary)', margin: '0 0 24px 0' }}>
          {t('maintenance.message')}
        </p>
        <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
          {t('maintenance.autoRefresh')}
        </p>
      </div>
    </div>
  );
}

function Screen404Lock({ injectName }: { injectName: string }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      background: '#0f172a',
      textAlign: 'center',
      padding: '20px',
      fontFamily: 'monospace',
    }}>
      <div style={{ color: '#475569', fontSize: '120px', fontWeight: '900', lineHeight: 1, marginBottom: '16px' }}>
        404
      </div>
      <h2 style={{ color: '#94a3b8', fontSize: '1.25rem', margin: '0 0 12px 0', fontWeight: 400 }}>
        This service is not reachable
      </h2>
      <p style={{ color: '#475569', fontSize: '0.875rem', margin: '0', maxWidth: '400px' }}>
        The requested resource could not be found on this server.
      </p>
      {injectName && (
        <p style={{ color: '#334155', fontSize: '0.75rem', marginTop: '48px', fontFamily: 'monospace' }}>
          ERR_CONNECTION_REFUSED
        </p>
      )}
    </div>
  );
}

function ServerDownLock({ injectName }: { injectName: string }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      background: 'var(--bg-page)',
      textAlign: 'center',
      padding: '20px',
    }}>
      <div style={{
        background: 'var(--bg-surface)',
        borderRadius: '12px',
        padding: '48px',
        maxWidth: '480px',
        boxShadow: 'var(--shadow-lg)',
        border: '2px solid var(--color-danger)',
      }}>
        <img
          src="/logo_arcos.webp"
          alt="ARCOS"
          style={{ width: '64px', height: '64px', marginBottom: '24px', opacity: 0.6 }}
          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
        <h2 style={{ margin: '0 0 12px 0', color: 'var(--text-primary)', fontSize: '1.25rem' }}>
          ARCOS
        </h2>
        <p style={{ color: 'var(--color-danger)', fontWeight: 600, margin: '0 0 8px 0', fontSize: '1rem' }}>
          Server down...
        </p>
        <p style={{ color: 'var(--text-secondary)', margin: '0', fontSize: '0.875rem' }}>
          The system is currently unavailable. Please wait and try again later.
        </p>
        {injectName && (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '24px' }}>
            {injectName}
          </p>
        )}
      </div>
    </div>
  );
}

function LoadingFallback() {
  const { t } = useTranslation();
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '200px',
      color: 'var(--text-muted)',
      fontSize: '14px'
    }}>
      {t('common.loading')}
    </div>
  );
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
      <h2 style={{ color: 'var(--text-primary)', marginBottom: '10px' }}>{t('common.pageNotFound')}</h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
        {t('common.pageNotFoundDesc')}
      </p>
      <a href="/" style={{ color: 'var(--color-primary)' }}>{t('common.backToDashboard')}</a>
    </div>
  );
}

// Routes that are blocked on read-only instances (write-only pages)
const READ_ONLY_BLOCKED_PATTERNS = [
  '/requests/new',
  '/edit',
  '/offers/create',
  '/extend-transport',
];

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isImpersonating, isReadOnlyInstance } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingFallback />;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Force password change redirect (skip when EXCON/ADMIN is impersonating)
  if (user.mustChangePassword && !isImpersonating && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" />;
  }

  // Block write-only routes on read-only instances
  if (isReadOnlyInstance) {
    const blocked = READ_ONLY_BLOCKED_PATTERNS.some(p => location.pathname.includes(p));
    if (blocked) {
      return <Navigate to="/requests" />;
    }
  }

  return <>{children}</>;
}

function App() {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [injectLock, setInjectLock] = useState<{ screenType: InjectScreenType; name: string } | null>(null);

  useEffect(() => {
    const handleMaintenance = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        setMaintenanceMode(true);
        // Poll for recovery every 5 seconds
        const interval = setInterval(async () => {
          try {
            const res = await fetch('/api/exercise/status');
            const data = await res.json();
            if (!data.maintenanceMode) {
              setMaintenanceMode(false);
              clearInterval(interval);
              window.location.reload();
            }
          } catch {
            // Server might be down, keep polling
          }
        }, 5000);
      }
    };

    window.addEventListener('maintenance-mode', handleMaintenance);
    return () => window.removeEventListener('maintenance-mode', handleMaintenance);
  }, []);

  const checkInject = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return; // not logged in
    try {
      const res = await injectsApi.check();
      const data = res.data;
      if (data.locked && data.screenType) {
        setInjectLock({ screenType: data.screenType, name: data.injectName || '' });
      } else {
        setInjectLock(null);
      }
    } catch {
      // Fail open — don't lock out if check fails
    }
  }, []);

  // SSE push — immediately react when any inject changes (create/update/delete)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const url = `/api/injects/sse?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);

    es.onmessage = (event) => {
      if (event.data === 'check' || event.data === 'connected') {
        checkInject();
      }
    };

    // EventSource auto-reconnects on error — no manual handling needed
    return () => es.close();
  }, [checkInject]);

  // Fallback poll every 60s (catches time-based inject start/end without a push event)
  useEffect(() => {
    checkInject();
    const interval = setInterval(checkInject, 60000);
    return () => clearInterval(interval);
  }, [checkInject]);

  if (maintenanceMode) {
    return <MaintenanceScreen />;
  }

  if (injectLock) {
    if (injectLock.screenType === 'SCREEN_404') {
      return <Screen404Lock injectName={injectLock.name} />;
    }
    return <ServerDownLock injectName={injectLock.name} />;
  }

  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="requests" element={<RequestsList />} />
              <Route path="requests/:id" element={<RequestDetail />} />
              <Route path="requests/:id/manage-offers" element={<ManageOffers />} />
              <Route path="requests/new" element={<CreateRequest />} />
              <Route path="requests/:id/edit" element={<EditRequest />} />
              <Route path="requests/:requestId/offers/create" element={<OfferCreate />} />
              <Route path="requests/:requestId/offers/:offerId/extend-transport" element={<TransportExtension />} />
              <Route path="transport-planning" element={<TransportPlanning />} />
              <Route path="transport-booking/:requestId" element={<TransportBooking />} />
              <Route path="users" element={<Users />} />
              <Route path="assets" element={<Assets />} />
              <Route path="startpoints" element={<StartPoints />} />
              <Route path="hospital" element={<Hospital />} />
              <Route path="schedules" element={<Schedules />} />
              <Route path="schedules/:id" element={<ScheduleDetail />} />
              <Route path="capacity-planning" element={<CapacityPlanning />} />
              <Route path="map" element={<Map />} />
              <Route path="statistics" element={<Navigate to="/exercise-analytics" replace />} />
              <Route path="flow" element={<FlowDiagram />} />
              <Route path="notification-settings" element={<NotificationSettings />} />
              <Route path="documentation" element={<Documentation />} />
              <Route path="change-password" element={<ChangePassword />} />
              <Route path="settings" element={<Settings />} />
              <Route path="my-patients" element={<MyPatients />} />
              <Route path="my-transport-patients" element={<MyTransportPatients />} />
              <Route path="missions" element={<Missions />} />
              <Route path="missions/:id" element={<MissionDetail />} />
              <Route path="patient-import" element={<PatientImport />} />
              <Route path="asset-templates" element={<AssetTemplates />} />
              <Route path="day-templates" element={<DayTemplates />} />
              <Route path="exercise-control" element={<ExerciseControl />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="audit-logs" element={<AuditLogs />} />
              <Route path="data-sync" element={<DataSync />} />
              <Route path="system-health" element={<HealthDashboard />} />
              <Route path="announcement-ticker" element={<AnnouncementTicker />} />
              <Route path="exercise-stats" element={<Navigate to="/exercise-analytics" replace />} />
              <Route path="exercise-analytics" element={<ExerciseAnalytics />} />
              <Route path="excon-statistics" element={<ExconStatistics />} />
              <Route path="nation-analytics" element={<Navigate to="/exercise-analytics" replace />} />
              <Route path="data-consistency" element={<DataConsistency />} />
              <Route path="inject-control" element={<InjectControl />} />
              <Route path="asset-contracts" element={<AssetContracts />} />
              <Route path="support" element={<Documentation />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
